import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { makeT } from "@/lib/i18n";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const [lang, setLangS] = useState(localStorage.getItem("lang") || "en");
  const [dark, setDark] = useState(localStorage.getItem("theme") === "dark");
  const [preview, setPreview] = useState(() => JSON.parse(sessionStorage.getItem("previewAs") || "null"));
  const t = useMemo(() => makeT(lang), [lang]);

  const loadSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      const m = await api.get("/meta");
      setMeta(m.data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession, preview]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    sessionStorage.clear();
    setPreview(null);
    setUser(false);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const mins = meta?.idle_timeout_minutes || 15;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => logout(), mins * 60000);
    };
    const evs = ["mousemove", "keydown", "click", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      evs.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, meta, logout]);

  const value = {
    user, setUser, meta, setMeta, lang, t, dark, preview, loadSession, logout,
    setLang: (l) => { localStorage.setItem("lang", l); setLangS(l); },
    toggleDark: () => setDark((d) => !d),
    can: (perm) => !!user?.perms?.[perm] && !(preview && perm.startsWith("action:")),
    startPreview: (u) => { sessionStorage.setItem("previewAs", JSON.stringify({ id: u.id, name: u.name })); setPreview({ id: u.id, name: u.name }); },
    stopPreview: () => { sessionStorage.removeItem("previewAs"); setPreview(null); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
